/* eslint-disable*/
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./SubscriptionEdit.css";
import { API_URL } from "../config.js";
import {
  faCheckSquare,
  faChevronCircleLeft,
  faChevronLeft,
  faChevronRight,
  faMobileAlt,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import profileImg from "../assets/img/profile.png";
import blueTick from "../assets/img/bluetick.png";
import { showToast } from "../components/toast.jsx";
import "./SubscriptionEdit.css";
import { PermissionWrapper, hasPermission } from "../helpers/permission.js";
import PerformanceComparisonChart from "./PerformanceComparisonChart";
import LoadingSpinner from "../components/LoadingSpinner.jsx";
import "./subscriptionportfolio.css";

const isFieldEditable = (id, fieldName) => {
  if (!id) return true; // New subscriber - all fields editable
  return hasPermission(fieldName, "edit", true); // Default true for backward compatibility
};

const isFieldVisible = (fieldName) => {
  return hasPermission(fieldName, "view", true); // Default true for backward compatibility
};

// Permission-aware table row component for editable fields
const PermissionTableRow = ({
  fieldName,
  label,
  isEditing,
  onToggleEdit,
  children,
  id,
  colSpan = "2",
  rightContent = null,
}) => {
  if (!isFieldVisible(fieldName)) return null;

  const canEdit = isFieldEditable(id, fieldName);
  const hasHandler = typeof onToggleEdit === "function";
  const actualFieldName = fieldName.replace("subscriber_", ""); // Remove prefix for onClick
  const handleClick =
    canEdit && hasHandler ? () => onToggleEdit(actualFieldName) : undefined;

  return (
    <tr>
      <td width={rightContent ? "140" : ""}>{label}</td>
      <td
        className={canEdit && hasHandler ? "edtext" : ""}
        colSpan={rightContent ? "1" : colSpan}
        data-id={actualFieldName}
        onClick={handleClick}
        style={{ cursor: canEdit && hasHandler ? "pointer" : "default" }}
      >
        {children}
      </td>
      {rightContent && (
        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          {rightContent}
        </td>
      )}
    </tr>
  );
};

// Permission-aware input component for inline editing
const PermissionEditableField = ({
  fieldName,
  value,
  isEditing,
  editFieldName,
  onChange,
  onBlur,
  onKeyDown,
  type = "text",
  placeholder = "",
  id,
}) => {
  if (!isFieldVisible(fieldName)) return null;

  const canEdit = isFieldEditable(id, fieldName);

  if (isEditing === editFieldName && canEdit) {
    return (
      <span className="edit">
        <input
          type={type}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoFocus
        />
      </span>
    );
  }

  return <span className="value">{value}</span>;
};

// Permission-aware dropdown component for inline editing
const PermissionEditableDropdown = ({
  fieldName,
  value,
  isEditing,
  editFieldName,
  onChange,
  onBlur,
  options = [],
  optionValue = "id",
  optionLabel = "pick_name",
  placeholder = "Select",
  id,
}) => {
  if (!isFieldVisible(fieldName)) return null;

  const canEdit = isFieldEditable(id, fieldName);

  if (isEditing === editFieldName && canEdit) {
    return (
      <span className="edit">
        <select value={value} onChange={onChange} onBlur={onBlur} autoFocus>
          <option value="">Select</option>
          {options.map((item, index) => (
            <option key={index} value={item[optionValue]}>
              {item[optionLabel]}
            </option>
          ))}
        </select>
      </span>
    );
  }

  // Display selected value
  const selectedItem = options.find((item) => item[optionValue] == value);
  const displayValue = selectedItem ? selectedItem[optionLabel] : value;

  return <span className="value">{displayValue}</span>;
};

const SubscriptionEdit = () => {
  const { id } = useParams();
  const location = useLocation();
  const filters = location.state?.filterParams || {};
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const chartRef = useRef(null);
  const [isRendering, setIsRendering] = useState(false); // New state for rendering
  const tableRenderedRef = useRef(false); // Ref to track table rendering

  const [userIds, setUserIds] = useState([]);

  const currentIndex = userIds.findIndex(
    (userId) => String(userId) === String(id)
  );

  const previousId = userIds[currentIndex - 1] ?? null;
  const nextId = userIds[currentIndex + 1] ?? null;
  const [currentHistoryPage, setCurrentHistoryPage] = useState(1);
  const [historyItemsPerPage] = useState(10); // Show 5 items per page

  // State for all data
  const [subscription, setSubscription] = useState({
    subscriptionid: "",
    leadsource: "",
    subscriberid: "",
    fullname: "",
    email: "",
    mobileno: "",
    verifyuser: "",
    serviceid: "",
    service_name: "",
    status: "",
    state_code: "",
    state_name: "",
    stype: "",
    plantype: "",
    servicetype: "",
    amount: "",
    gstin: "",
    billedto: "",
    trades_total: "",
    activation_date: "",
    expiry_date: "",
    renew_date: "",
    order_approval: "",
    assigned_user: "",
    assigned_user_name: "",
    sales_manager: "",
    sales_manager_name: "",
    pay_date: "",
    pay_mode: "",
    pay_refno: "",
    lead_status: "",
    reason_not_interested: "",
    next_followup: "",
    created_on: "",
    updated_on: "",
    rationale_file: "",
    orderid: "",
    oid: "",
    appinstalled: "No",
    token: "",
    plantype_disp: "Monthly",
    invest_status: 0,
    ordersub_refno: "",
  });

  const [loading, setLoading] = useState(false);
  const [commentList, setCommentList] = useState([]);
  const [activityList, setActivityList] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [errorMessages, setErrorMessages] = useState({});
  const [subHistoryList, setSubHistoryList] = useState([]);

  // Performance data
  const [performanceData, setPerformanceData] = useState({
    totalcount: 0,
    positivecount: 0,
    negativecount: 0,
    profitvalue: 0,
    lossvalue: 0,
    totalprofit: "0",
    totalloss: "0",
    avgprofit: "0",
    accuracy: 0,
  });

  const [dropdownOptions, setDropdownOptions] = useState({
    leadSourceList: [],
    serviceList: [],
    subscriptionTypeList: [],
    serviceStatusList: [],
    serviceTypeList: [],
    userList: [],
    fullUserList: [],
    salesManagerList: [],
    utmSourceList: [],
    utmMediumList: [],
    utmCampaignList: [],
    utmContentList: [],
    utmTermList: [],
    languageList: [],
    stateList: [],
    paidTradesList: [],
    approvalList: [],
    templatePushNotificationList: [],
    templateSmsList: [],
    leadStatusList: [],
  });

  // Format date for display
  const formatDateDisplay = (dateStr) => {
    if (!dateStr || dateStr === "0000-00-00") return "";
    return moment(dateStr).format("DD-MM-YYYY");
  };

  // Format datetime for display
  const formatDateTimeDisplay = (datetimeStr) => {
    if (!datetimeStr || datetimeStr === "0000-00-00 00:00:00") return "";
    return moment(datetimeStr).format("ddd MMM DD YYYY HH:mm A");
  };

  const fetchData = async () => {
    setLoading(true);
    setIsRendering(true); // Start rendering state
    tableRenderedRef.current = false; // Reset rendering flag
    try {
      const response = await axios.get(`${API_URL}subscription/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        params: location.state?.filterParams,
      });

      const subscriptionData = response.data.data.subscription;

      // Set subscription data with proper field mapping
      setSubscription({
        subscriptionid: subscriptionData.subscriptionid || "",
        leadsource: subscriptionData.subscriber_lead_source || "",
        subscriberid: subscriptionData.subscriberid || "",
        fullname: subscriptionData.fullname || "",
        email: subscriptionData.email || "",
        mobileno: subscriptionData.mobileno || "",
        verifyuser: subscriptionData.verifyuser || 0,
        serviceid: subscriptionData.serviceid || "",
        service_name: subscriptionData.service_service_name || "",
        status: subscriptionData.status || "",
        state_code: subscriptionData.state_code || "",
        state_name: subscriptionData.state_name || "",
        stype: subscriptionData.stype || "",
        plantype: subscriptionData.plantype || "",
        servicetype: subscriptionData.servicetype || "",
        amount: subscriptionData.amount || "",
        gstin: subscriptionData.gstin || "",
        billedto: subscriptionData.billedto || "",
        trades_total: subscriptionData.trades_total || "",
        activation_date: subscriptionData.activation_date || "",
        expiry_date: subscriptionData.expiry_date || "",
        renew_date: subscriptionData.renew_date || "",
        order_approval: subscriptionData.order_approval || "",
        assigned_user: subscriptionData.assigned_user || "",
        assigned_user_name: subscriptionData.assigned_user_fullname || "",
        sales_manager: subscriptionData.sales_manager || "",
        sales_manager_name: subscriptionData.sales_manager_fullname || "",
        pay_date: subscriptionData.pay_date || "",
        pay_mode: subscriptionData.pay_mode || "",
        pay_refno: subscriptionData.pay_refno || "",
        lead_status: subscriptionData.subscriber_lead_status || "",
        reason_not_interested: subscriptionData.reason_not_interested || "",
        next_followup: subscriptionData.subscriber_next_followup || "",
        created_on: subscriptionData.created_on || "",
        updated_on: subscriptionData.updated_on || "",
        rationale_file: subscriptionData.service_rationale_file || "",
        orderid: subscriptionData.order_orderid || "",
        oid: subscriptionData.order_id || "",
        appinstalled: subscriptionData.token ? true : false,
        plantype_disp: subscriptionData.plantype == 0 ? "Credits" : "Monthly",
        invest_status: subscriptionData.invest_status || 0,
        ordersub_refno: subscriptionData.ordersub_refno || "",
        activation_date_disp: formatDateDisplay(
          subscriptionData.activation_date
        ),
        expiry_date_disp: formatDateDisplay(subscriptionData.expiry_date),
        renew_date_disp: formatDateDisplay(subscriptionData.renew_date),
        pay_date_disp: formatDateDisplay(subscriptionData.pay_date),
        next_followup_disp: formatDateDisplay(subscriptionData.next_followup),
        created_on_disp: formatDateTimeDisplay(subscriptionData.created_on),
        updated_on_disp:
          subscriptionData.updated_on &&
            subscriptionData.updated_on !== "0000-00-00 00:00:00"
            ? `Modified On : ${formatDateTimeDisplay(
              subscriptionData.updated_on
            )}`
            : "",
      });
      const ids = response.data?.data?.subscriptionIds || [];
      const normalizedIds = Array.isArray(ids)
        ? ids
          .map((val) => (isNaN(parseInt(val)) ? String(val) : parseInt(val)))
          .filter((v) => v !== null && v !== undefined && v !== "")
        : [];
      setUserIds(normalizedIds);
      setTimeout(() => {
        tableRenderedRef.current = true;
        checkRenderingStatus();
      }, 100);
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      tableRenderedRef.current = true;
      checkRenderingStatus();
    } finally {
      setLoading(false);
    }
  };

  const fetchCharData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}subscription/chart-data`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        params: {
          subscriptionId: id,
        },
      });

      const Performance = response.data.data;
      setPerformanceData({
        totalcount: performance.totalcount || 0,
        positivecount: Performance.positivecount || 0,
        negativecount: Performance.negativecount || 0,
        profitvalue: Performance.profitvalue || 0,
        lossvalue: Performance.lossvalue || 0,
        totalprofit: Performance.totalprofit || 0,
        totalloss: Performance.totalloss || 0,
        avgprofit: Performance.avgprofit || 0,
        accuracy: Performance.accuracy || 0,
      });

      // Update chart data
    } catch (error) {
      console.error("Error fetching subscription data:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkRenderingStatus = () => {
    if (tableRenderedRef.current) {
      // Add a small delay to ensure the table is fully rendered
      setTimeout(() => {
        setIsRendering(false);
      }, 300);
    }
  };

  const fetchDropdownData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}subscription/get-dropdown`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.success && response.data.data) {
        setDropdownOptions({
          leadSourceList: response.data.data.lead_sourcelist || [],
          serviceList: response.data.data.PortfolioServicelist || [],
          subscriptionTypeList: response.data.data.subscription_typelist || [],
          serviceStatusList: response.data.data.service_statuslist || [],
          serviceTypeList: response.data.data.service_typelist || [],
          userList: response.data.data.userlist || [],
          fullUserList: response.data.data.fulluserlist || [],
          salesManagerList: response.data.data.salesManagerlist || [],
          utmSourceList: response.data.data.utmsourcelist || [],
          utmMediumList: response.data.data.utmmediumlist || [],
          utmCampaignList: response.data.data.utmcampaignlist || [],
          utmContentList: response.data.data.utmcontentlist || [],
          utmTermList: response.data.data.utmtermlist || [],
          languageList: response.data.data.languagelist || [],
          stateList: response.data.data.statelist || [],
          paidTradesList: response.data.data.paidtradelist || [],
          approvalList: response.data.data.approvallist || [],
          templatePushNotificationList:
            response.data.data.templatePushNotificationlist || [],
          templateSmsList: response.data.data.templateSmslist || [],
          leadStatusList: response.data.data.lead_statuslist || [],
        });
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommentList = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}subscription/comments`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        params: {
          subscriberid: subscription.subscriberid,
        },
      });

      if (response.data.success && response.data.data) {
        setCommentList(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}subscription/get-subscription-logs/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success && response.data.data) {
        setActivityList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching subscription logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionHistory = async () => {
    try {
      setLoading(true);
      // Wait for subscription data to be available
      if (!subscription.subscriberid) {
        console.log("Waiting for subscriberid...");
        return;
      }

      const response = await axios.get(
        `${API_URL}subscription/subscription-history`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            // subscriberId: subscription.subscriberid,
            subscriptionId: id,
          },
        }
      );

      if (response.data.success && response.data.data) {
        console.log("History data received:", response.data.data); // Debug log
        // Format dates to DD-MM-YYYY before setting state
        const formattedHistory = response.data.data.map((item) => ({
          ...item,
          activedate: item.activedate
            ? moment(item.activedate).format("DD-MM-YYYY")
            : "-",
          expirydate: item.expirydate
            ? moment(item.expirydate).format("DD-MM-YYYY")
            : "-",
          created_on: item.created_on
            ? moment(item.created_on).format("DD-MM-YYYY")
            : "-",
        }));
        setSubHistoryList(formattedHistory);
      } else {
        console.log("No history data found");
        setSubHistoryList([]);
      }
    } catch (error) {
      console.error("Error fetching subscription history:", error);
      setSubHistoryList([]);
    } finally {
      setLoading(false);
    }
  };
  const handlePaymentNotInvested = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        `${API_URL}subscription/post-payment-not-invested?templateId=11&subscriberId=${subscription.subscriberid}`,
        {}, // empty body as per your curl example
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 201) {
        showToast.success(
          response.data.message ||
          "Post payment not invested process started successfully"
        );
      } else {
        showToast.error("Failed to start payment not invested process");
      }
    } catch (error) {
      console.error("Error starting payment not invested process:", error);
      showToast.error("An error occurred while starting the process");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchCharData();
    fetchDropdownData();
    fetchSubscriptionLogs();
    fetchSubscriptionHistory();
  }, [id]);
  useEffect(() => {
    if (subscription.subscriberid) {
      fetchCommentList();
      fetchSubscriptionHistory(); // Call it here after subscriberid is available
    }
  }, [subscription.subscriberid]);
  useEffect(() => {
    if (subscription.subscriberid) {
      fetchCommentList();
    }
  }, [subscription.subscriberid]);

  const postComment = async () => {
    if (!commentText.trim()) return;

    try {
      const response = await axios.post(
        `${API_URL}subscription/save-comment`,
        {
          id: String(subscription.subscriberid),
          fieldvalue: commentText,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        // Clear the comment text
        setCommentText("");
        fetchCommentList();
      } else {
        // Handle API error response
        console.error("API Error:", response.data.message);
        setErrorMessages((prev) => ({
          ...prev,
          commentPost: response.data.message || "Failed to post comment",
        }));
      }
    } catch (error) {
      console.error("Error posting comment:", error);
      setErrorMessages((prev) => ({
        ...prev,
        commentPost: error.message || "Failed to post comment",
      }));
    }
  };

  const [editField, setEditField] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [originalValues, setOriginalValues] = useState({}); // Store original values for revert functionality

  useEffect(() => {
    if (subscription && Object.keys(subscription).length > 0) {
      setFormValues({
        serviceid: subscription.serviceid || "",
        status: subscription.status || "",
        stype: subscription.stype || "",
        servicetype: subscription.servicetype || "",
        order_approval: subscription.order_approval || "",
        trades_total: subscription.trades_total || "",
        amount: subscription.amount || "",
        activation_date: subscription.activation_date || "",
        expiry_date: subscription.expiry_date || "",
        renew_date: subscription.renew_date || "",
        sales_manager: subscription.sales_manager || "",
        lead_status: subscription.lead_status || "",
        next_followup: subscription.next_followup || "",
      });
    }
  }, [subscription]);

  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (field) => {
    // Skip API if no real change
    const prevOriginal = Object.prototype.hasOwnProperty.call(
      originalValues,
      field
    )
      ? originalValues[field]
      : getCurrentFieldValue(field);
    const prevStr = (prevOriginal == null ? "" : String(prevOriginal)).trim();
    const nextStr = (
      formValues[field] == null ? "" : String(formValues[field])
    ).trim();
    if (prevStr === nextStr) {
      setEditField(null);
      return;
    }

    setLoading(true);
    setIsRendering(true);
    tableRenderedRef.current = false;

    console.log("Saving", field, formValues[field]);
    const payload = {
      fieldname: field,
      fieldvalue: formValues[field],
    };
    try {
      const response = await axios.put(
        `${API_URL}subscription/update-subscription/${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status == 200) {
        showToast.success("Updated Successfully");
        fetchData();
        fetchSubscriptionLogs();
        setEditField(null); // Exit edit mode
      } else {
        setLoading(false);
        setIsRendering(false);
      }
    } catch (error) {
      console.error("Error saving field:", error);
      showToast.error("Failed to update field");
      setLoading(false);
      setIsRendering(false);
    }
  };

  // Helper function to get current field value
  const getCurrentFieldValue = (fieldName) => {
    const fieldMap = {
      serviceid: subscription.serviceid,
      status: subscription.status,
      stype: subscription.stype,
      servicetype: subscription.servicetype,
      order_approval: subscription.order_approval,
      trades_total: subscription.trades_total,
      amount: subscription.amount,
      activation_date: subscription.activation_date,
      expiry_date: subscription.expiry_date,
      renew_date: subscription.renew_date,
      sales_manager: subscription.sales_manager,
      lead_status: subscription.lead_status,
      next_followup: subscription.next_followup,
    };
    return fieldMap[fieldName] || "";
  };

  // Helper function to revert field value
  const revertFieldValue = (fieldName, originalValue) => {
    setSubscription((prev) => ({
      ...prev,
      [fieldName]: originalValue,
    }));
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: originalValue,
    }));
  };

  // Handle blur event - revert to original value if no change was made
  const handleBlur = (fieldName) => {
    const currentValue = getCurrentFieldValue(fieldName);
    const originalValue = originalValues[fieldName];

    // If value hasn't changed, revert to original
    if (
      currentValue === originalValue ||
      currentValue === "" ||
      currentValue === null
    ) {
      revertFieldValue(fieldName, originalValue);
    }

    // Clear editing state
    setEditField(null);
  };

  // Enhanced toggle edit function to store original values
  const handleToggleEdit = (fieldName) => {
    // Store original value before editing
    const currentValue = getCurrentFieldValue(fieldName);
    setOriginalValues((prev) => ({
      ...prev,
      [fieldName]: currentValue,
    }));
    setEditField(fieldName);
  };

  const MorrisDonutChart = ({ performanceData }) => {
    const chartRef = useRef(null);
    const [showFallback, setShowFallback] = useState(false);

    useEffect(() => {
      let retryCount = 0;
      const maxRetries = 20;

      const profit = performanceData.profitvalue || 0;
      const loss = performanceData.lossvalue || 0;

      // Show fallback if data is 0 or missing
      if (profit + loss === 0) {
        setShowFallback(true);
        return;
      } else {
        setShowFallback(false);
      }

      const drawChart = () => {
        if (window.Morris && window.Morris.Donut && chartRef.current) {
          chartRef.current.innerHTML = "";

          new window.Morris.Donut({
            element: chartRef.current,
            data: [
              { label: "Profit", value: profit },
              { label: "Loss", value: loss },
            ],
            colors: ["#6fca00", "#e03e25"],
            resize: true,
            labelColor: "#8c9fc3",
          });
        } else if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(drawChart, 100);
        }
      };

      drawChart();

      return () => {
        if (chartRef.current) {
          chartRef.current.innerHTML = "";
        }
      };
    }, [performanceData]);

    return (
      <div className="dochart">
        {showFallback ? (
          <div
            className="text-center d-flex align-items-center justify-content-center fw-bold"
            style={{
              height: "100%",
              fontSize: "40px",
              fontFamily: "open sans",
              color: "#8c9fc3",
            }}
          >
            Profit
            <br />0
          </div>
        ) : (
          <div ref={chartRef} className="dochart" />
        )}
      </div>
    );
  };

  const CircularProgress = ({ label = "Accuracy", percent = 0 }) => {
    const strokeWidth = 8;
    const radius = 35;
    const normalizedRadius = radius - strokeWidth / 2;
    const circumference = 2 * Math.PI * normalizedRadius;
    const strokeDashoffset = circumference * (1 - percent / 100);

    return (
      <div
        className="progress--circle progress--25"
        style={{ padding: "10px" }}
      >
        <svg
          className="progress green noselect"
          x="0px"
          y="0px"
          viewBox="0 0 80 80"
        >
          {/* Track */}
          <circle
            cx="40"
            cy="40"
            r={normalizedRadius}
            // stroke="#e6e6e6"
            fill="none"
            strokeWidth={strokeWidth}
          />

          {/* Fill */}
          <circle
            cx="40"
            cy="40"
            r={normalizedRadius}
            stroke="#6fca00"
            fill="none"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 40 40)"
            style={{ transition: "stroke-dashoffset 0.5s ease" }}
          />

          <text className="text" x="50%" y="44%">
            {label}
          </text>
          <text className="value" x="50%" y="65%">
            {percent}%
          </text>
        </svg>
      </div>
    );
  };

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      {/* {loading && <div className="loading">Loading…</div>} */}
      {(loading || isRendering)}

      {/* Header would be included here */}

      <div id="content" className="contentrrrrrrr">
        <div className="row pb-2">
          <div className="col-md-4">
            <img
              src={profileImg}
              alt="Profile"
              style={{ width: "32px", marginRight: "10px" }}
            />
            {hasPermission("subscriber_fullname", "view") &&
              (hasPermission("subscriber", "edit") ? (
                <Link
                  to={`/Subscriber/Edit/${subscription.subscriberid}`}
                  state={
                    location.state?.subscriberFilterParams
                      ? { filterParams: location.state.subscriberFilterParams }
                      : undefined
                  }
                >
                  <span id="subscribername">{subscription.fullname}</span>
                </Link>
              ) : (
                <span id="subscribername">{subscription.fullname}</span>
              ))}
            {hasPermission("subscriber_mobileno", "view") && (
              <>
                <FontAwesomeIcon
                  icon={faMobileAlt}
                  className={
                    subscription.appinstalled ? "text-success" : "text-danger"
                  }
                  style={{
                    fontSize: "1.5rem",
                    lineHeight: "1.5rem",
                    marginLeft: "5px",
                  }}
                />{" "}
                {subscription.mobileno}
              </>
            )}
            {subscription.verifyuser === 1 && (
              <img
                src={blueTick}
                alt="Verified"
                style={{ width: "20px", marginTop: "-10px", marginLeft: "5px" }}
                title="Verified"
              />
            )}
            {subscription.invest_status > 0 && (
              <FontAwesomeIcon
                icon={faChartLine}
                className="text-success ms-1"
                style={{ fontSize: "16px", marginTop: "-5px" }}
                title="Has Investments"
              />
            )}
          </div>
          <div className="col-md-8" style={{ textAlign: "right" }}>
            {nextId !== 0 && nextId !== "" && nextId !== null && (
              <button
                className="btn btn-default btn-sm pull-right mx-1 my-1"
                onClick={() =>
                  navigate(`/SubscriptionPortfolio/edit/${nextId}`, {
                    state: { filterParams: filters },
                  })
                }
                title="Next Subscription"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            )}
            {previousId !== 0 && previousId !== "" && previousId !== null && (
              <button
                className="btn btn-default btn-sm pull-right mx-1 my-1"
                onClick={() =>
                  navigate(`/SubscriptionPortfolio/edit/${previousId}`, {
                    state: { filterParams: filters },
                  })
                }
                title="Previous Subscription"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            )}

            {subscription.invest_status === 0 && (
              <button
                className="btn btn-default btn-sm mx-1"
                onClick={handlePaymentNotInvested}
              >
                Send Investment Reminder
              </button>
            )}
            <button
              className="btn btn-default btn-sm pull-right ml-1"
              onClick={() => navigate("/SubscriptionPortfolio/")}
              title="Go Back To Subscription Portfolio"
            >
              <FontAwesomeIcon icon={faChevronCircleLeft} /> Go Back
            </button>

            {subscription.amount > 100 && (
              <>
                {/* <a className="btn btn-default btn-sm pull-right ml-1"
                  href={`/Orders/sendInvoiceEmail/${subscription.oid}`}
                  title="Send Invoice"
                >
                  <i className="fa fa-envelope"></i> Send Invoice
                </a>
                <a className="btn btn-default btn-sm pull-right ml-1"
                  href={`/Orders/downloadInvoice/${subscription.oid}`}
                  title="Download Invoice"
                >
                  <i className="fa fa-download"></i> Download Invoice
                </a> */}
              </>
            )}

            {/* {subscription.rationale_file && (
              <a
                className="btn btn-default btn-sm pull-end me-1"
                download
                href={`/uploads/${subscription.rationale_file}`}
                title="Send Rationale"
              >
                <FontAwesomeIcon icon={faEnvelope} /> Send Rationale
              </a>
            )} */}
          </div>
        </div>

        <div className="row">
          <div className="col-md-5">
            <div className="panel panel-default with-rounded-corner with-shadow">
              <div className="panel-body">
                <table style={{ fontSize: "11px", color: "#808080" }}>
                  <tbody>
                    <tr>
                      <td>Created On : {subscription.created_on_disp}</td>
                    </tr>
                    <tr>
                      <td id="modifiedtime">{subscription.updated_on_disp}</td>
                    </tr>
                  </tbody>
                </table>

                <table className="table table-condensed table-hover bg-white usertable">
                  <tbody id="userDetailTable">
                    <tr>
                      <td width="140">Subscription ID</td>
                      <td>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : (
                          subscription.subscriptionid
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td width="140">Lead Source</td>
                      <td>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : (
                          subscription.leadsource
                        )}
                      </td>
                    </tr>

                    <PermissionTableRow
                      fieldName="subscription_service"
                      label="Service"
                      isEditing={editField === "serviceid"}
                      onToggleEdit={() => handleToggleEdit("serviceid")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "serviceid" ? (
                        <select
                          className="form-control"
                          value={formValues.serviceid}
                          onChange={(e) =>
                            handleChange("serviceid", e.target.value)
                          }
                          onBlur={() => {
                            if (
                              formValues.serviceid === "" ||
                              formValues.serviceid === originalValues.serviceid
                            ) {
                              handleBlur("serviceid");
                            } else {
                              handleSave("serviceid");
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (
                                formValues.serviceid === "" ||
                                formValues.serviceid ===
                                originalValues.serviceid
                              ) {
                                handleBlur("serviceid");
                              } else {
                                handleSave("serviceid");
                              }
                            }
                          }}
                          autoFocus
                        >
                          <option value="">Select</option>
                          {dropdownOptions.serviceList.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.service_name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>
                          {
                            dropdownOptions.serviceList.find(
                              (opt) => opt.id == formValues.serviceid
                            )?.service_name
                          }
                        </span>
                      )}
                    </PermissionTableRow>

                    {/* Status Field with Permission */}
                    <PermissionTableRow
                      fieldName="subscription_status"
                      label="Status"
                      isEditing={editField === "status"}
                      onToggleEdit={() => handleToggleEdit("status")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "status" ? (
                        <select
                          className="form-control"
                          value={formValues.status}
                          onChange={(e) =>
                            handleChange("status", e.target.value)
                          }
                          onBlur={() => {
                            if (
                              formValues.status === "" ||
                              formValues.status === originalValues.status
                            ) {
                              handleBlur("status");
                            } else {
                              handleSave("status");
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (
                                formValues.status === "" ||
                                formValues.status === originalValues.status
                              ) {
                                handleBlur("status");
                              } else {
                                handleSave("status");
                              }
                            }
                          }}
                          autoFocus
                        >
                          <option value="">Select</option>
                          {dropdownOptions.serviceStatusList.map((opt) => (
                            <option key={opt.pick_name} value={opt.pick_name}>
                              {opt.pick_name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>
                          {
                            dropdownOptions.serviceStatusList.find(
                              (opt) => opt.pick_name == formValues.status
                            )?.pick_name
                          }
                        </span>
                      )}
                    </PermissionTableRow>

                    {/* Subscription Type Field with Permission */}
                    <PermissionTableRow
                      fieldName="subscription_type"
                      label="Subscription Type"
                      isEditing={editField === "stype"}
                      onToggleEdit={() => handleToggleEdit("stype")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "stype" ? (
                        <select
                          className="form-control"
                          value={formValues.stype}
                          onChange={(e) =>
                            handleChange("stype", e.target.value)
                          }
                          onBlur={() => {
                            if (
                              formValues.stype === "" ||
                              formValues.stype === originalValues.stype
                            ) {
                              handleBlur("stype");
                            } else {
                              handleSave("stype");
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (
                                formValues.stype === "" ||
                                formValues.stype === originalValues.stype
                              ) {
                                handleBlur("stype");
                              } else {
                                handleSave("stype");
                              }
                            }
                          }}
                          autoFocus
                        >
                          <option value="">Select</option>
                          {dropdownOptions.subscriptionTypeList.map((opt) => (
                            <option key={opt.pick_name} value={opt.pick_name}>
                              {opt.pick_name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>
                          {
                            dropdownOptions.subscriptionTypeList.find(
                              (opt) => opt.pick_name == formValues.stype
                            )?.pick_name
                          }
                        </span>
                      )}
                    </PermissionTableRow>

                    {/* Service Type Field with Permission */}
                    <PermissionTableRow
                      fieldName="subscription_service_type"
                      label="Service Type"
                      isEditing={editField === "servicetype"}
                      onToggleEdit={() => handleToggleEdit("servicetype")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "servicetype" ? (
                        <select
                          className="form-control"
                          value={formValues.servicetype}
                          onChange={(e) =>
                            handleChange("servicetype", e.target.value)
                          }
                          onBlur={() => {
                            if (
                              formValues.servicetype === "" ||
                              formValues.servicetype ===
                              originalValues.servicetype
                            ) {
                              handleBlur("servicetype");
                            } else {
                              handleSave("servicetype");
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (
                                formValues.servicetype === "" ||
                                formValues.servicetype ===
                                originalValues.servicetype
                              ) {
                                handleBlur("servicetype");
                              } else {
                                handleSave("servicetype");
                              }
                            }
                          }}
                          autoFocus
                        >
                          <option value="">Select</option>
                          {dropdownOptions.serviceTypeList.map((opt) => (
                            <option key={opt.pick_name} value={opt.pick_name}>
                              {opt.pick_name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>
                          {
                            dropdownOptions.serviceTypeList.find(
                              (opt) => opt.pick_name == formValues.servicetype
                            )?.pick_name
                          }
                        </span>
                      )}
                    </PermissionTableRow>

                    {/* Order Approval with Permission */}
                    <PermissionTableRow
                      fieldName="subscription_order_approval"
                      label="Order Approval"
                      isEditing={editField === "order_approval"}
                      onToggleEdit={() => handleToggleEdit("order_approval")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "order_approval" ? (
                        <select
                          className="form-control"
                          value={formValues.order_approval}
                          onChange={(e) =>
                            handleChange("order_approval", e.target.value)
                          }
                          onBlur={() => {
                            if (
                              formValues.order_approval === "" ||
                              formValues.order_approval ===
                              originalValues.order_approval
                            ) {
                              handleBlur("order_approval");
                            } else {
                              handleSave("order_approval");
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (
                                formValues.order_approval === "" ||
                                formValues.order_approval ===
                                originalValues.order_approval
                              ) {
                                handleBlur("order_approval");
                              } else {
                                handleSave("order_approval");
                              }
                            }
                          }}
                          autoFocus
                        >
                          <option value="">Select</option>
                          {dropdownOptions.approvalList.map((opt) => (
                            <option key={opt.pick_name} value={opt.pick_name}>
                              {opt.pick_name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>
                          {
                            dropdownOptions.approvalList.find(
                              (opt) =>
                                opt.pick_name == formValues.order_approval
                            )?.pick_name
                          }
                        </span>
                      )}
                    </PermissionTableRow>

                    {/* Trades Total with Permission - Conditionally Rendered */}
                    {subscription.plantype == 0 && (
                      <PermissionTableRow
                        fieldName="subscription_trades_total"
                        label="Trades Total"
                        isEditing={editField === "trades_total"}
                        onToggleEdit={() => handleToggleEdit("trades_total")}
                        id={id}
                        colSpan="2"
                      >
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : editField === "trades_total" ? (
                          <input
                            type="text"
                            className="form-control"
                            value={formValues.trades_total}
                            onChange={(e) =>
                              handleChange("trades_total", e.target.value)
                            }
                            onBlur={() => {
                              if (
                                formValues.trades_total === "" ||
                                formValues.trades_total ===
                                originalValues.trades_total
                              ) {
                                handleBlur("trades_total");
                              } else {
                                handleSave("trades_total");
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                if (
                                  formValues.trades_total === "" ||
                                  formValues.trades_total ===
                                  originalValues.trades_total
                                ) {
                                  handleBlur("trades_total");
                                } else {
                                  handleSave("trades_total");
                                }
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          <span>{formValues.trades_total}</span>
                        )}
                      </PermissionTableRow>
                    )}

                    {/* Amount with Permission */}
                    <PermissionTableRow
                      fieldName="subscription_amount"
                      label="Amount"
                      isEditing={editField === "amount"}
                      onToggleEdit={() => handleToggleEdit("amount")}
                      id={id}
                      colSpan="2"
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "amount" ? (
                        <input
                          type="text"
                          className="form-control"
                          value={formValues.amount}
                          onChange={(e) =>
                            handleChange("amount", e.target.value)
                          }
                          onBlur={() => {
                            if (
                              formValues.amount === "" ||
                              formValues.amount === originalValues.amount
                            ) {
                              handleBlur("amount");
                            } else {
                              handleSave("amount");
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (
                                formValues.amount === "" ||
                                formValues.amount === originalValues.amount
                              ) {
                                handleBlur("amount");
                              } else {
                                handleSave("amount");
                              }
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        <span>{formValues.amount}</span>
                      )}
                    </PermissionTableRow>

                    {/* Activation Date with Permission */}
                    <PermissionTableRow
                      fieldName="subscription_activation_date"
                      label="Activation Date"
                      isEditing={editField === "activation_date"}
                      onToggleEdit={() => handleToggleEdit("activation_date")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "activation_date" ? (
                        <DatePicker
                          selected={
                            formValues.activation_date
                              ? new Date(formValues.activation_date)
                              : null
                          }
                          onChange={(date) =>
                            handleChange(
                              "activation_date",
                              moment(date).format("YYYY-MM-DD")
                            )
                          }
                          onBlur={() => {
                            if (
                              formValues.activation_date === "" ||
                              formValues.activation_date ===
                              originalValues.activation_date
                            ) {
                              handleBlur("activation_date");
                            } else {
                              handleSave("activation_date");
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (
                                formValues.activation_date === "" ||
                                formValues.activation_date ===
                                originalValues.activation_date
                              ) {
                                handleBlur("activation_date");
                              } else {
                                handleSave("activation_date");
                              }
                            }
                          }}
                          className="form-control"
                          dateFormat="dd-MM-yyyy"
                          autoFocus
                        />
                      ) : (
                        <span>
                          {formatDateDisplay(formValues.activation_date)}
                        </span>
                      )}
                    </PermissionTableRow>

                    {/* Expiry Date with Permission */}
                    <PermissionTableRow
                      fieldName="subscription_expiry_date"
                      label="Expiry Date"
                      isEditing={editField === "expiry_date"}
                      onToggleEdit={() => handleToggleEdit("expiry_date")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "expiry_date" ? (
                        <DatePicker
                          selected={
                            formValues.expiry_date
                              ? new Date(formValues.expiry_date)
                              : null
                          }
                          onChange={(date) =>
                            handleChange(
                              "expiry_date",
                              moment(date).format("YYYY-MM-DD")
                            )
                          }
                          onBlur={() => {
                            if (
                              formValues.expiry_date === "" ||
                              formValues.expiry_date ===
                              originalValues.expiry_date
                            ) {
                              handleBlur("expiry_date");
                            } else {
                              handleSave("expiry_date");
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (
                                formValues.expiry_date === "" ||
                                formValues.expiry_date ===
                                originalValues.expiry_date
                              ) {
                                handleBlur("expiry_date");
                              } else {
                                handleSave("expiry_date");
                              }
                            }
                          }}
                          className="form-control"
                          dateFormat="dd-MM-yyyy"
                          autoFocus
                        />
                      ) : (
                        <span>{formatDateDisplay(formValues.expiry_date)}</span>
                      )}
                    </PermissionTableRow>

                    {/* Renew Date with Permission */}
                    <PermissionTableRow
                      fieldName="subscription_renew_date"
                      label="Renew Date"
                      isEditing={editField === "renew_date"}
                      onToggleEdit={() => handleToggleEdit("renew_date")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "renew_date" ? (
                        <DatePicker
                          selected={
                            formValues.renew_date
                              ? new Date(formValues.renew_date)
                              : null
                          }
                          onChange={(date) =>
                            handleChange(
                              "renew_date",
                              moment(date).format("YYYY-MM-DD")
                            )
                          }
                          onBlur={() => {
                            if (
                              formValues.renew_date === "" ||
                              formValues.renew_date ===
                              originalValues.renew_date
                            ) {
                              handleBlur("renew_date");
                            } else {
                              handleSave("renew_date");
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (
                                formValues.renew_date === "" ||
                                formValues.renew_date ===
                                originalValues.renew_date
                              ) {
                                handleBlur("renew_date");
                              } else {
                                handleSave("renew_date");
                              }
                            }
                          }}
                          className="form-control"
                          dateFormat="dd-MM-yyyy"
                          autoFocus
                        />
                      ) : (
                        <span>{formatDateDisplay(formValues.renew_date)}</span>
                      )}
                    </PermissionTableRow>

                    {/* Sales Manager with Permission */}
                    <PermissionTableRow
                      fieldName="subscription_sales_manager"
                      label="Sales Manager"
                      isEditing={editField === "sales_manager"}
                      onToggleEdit={() => handleToggleEdit("sales_manager")}
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : editField === "sales_manager" ? (
                        <select
                          className="form-control"
                          value={formValues.sales_manager}
                          onChange={(e) =>
                            handleChange("sales_manager", e.target.value)
                          }
                          onBlur={() => {
                            if (
                              formValues.sales_manager === "" ||
                              formValues.sales_manager ===
                              originalValues.sales_manager
                            ) {
                              handleBlur("sales_manager");
                            } else {
                              handleSave("sales_manager");
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (
                                formValues.sales_manager === "" ||
                                formValues.sales_manager ===
                                originalValues.sales_manager
                              ) {
                                handleBlur("sales_manager");
                              } else {
                                handleSave("sales_manager");
                              }
                            }
                          }}
                          autoFocus
                        >
                          <option value="">Select</option>
                          {dropdownOptions.salesManagerList.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.user_fullname}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>
                          {
                            dropdownOptions.salesManagerList.find(
                              (opt) => opt.id == formValues.sales_manager
                            )?.user_fullname
                          }
                        </span>
                      )}
                    </PermissionTableRow>

                    {/* Assigned User with Permission (read-only) */}
                    <PermissionTableRow
                      fieldName="subscription_assigned_user"
                      label="Assigned User"
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : (
                        subscription.assigned_user_name
                      )}
                    </PermissionTableRow>

                    {/* Order ID with Permission (read-only with link) */}
                    <PermissionTableRow
                      fieldName="subscription_assigned_user"
                      label="Order ID"
                      id={id}
                    >
                      {loading || isRendering ? (
                        <div
                          className="skeleton-text"
                          style={{
                            width: "100%",
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                          }}
                        ></div>
                      ) : (
                        <a href={`/Order/Edit/${subscription.oid}`}>
                          {subscription.ordersub_refno}
                        </a>
                      )}
                    </PermissionTableRow>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="panel panel-default with-rounded-corner with-shadow">
              <div className="panel-body">
                <span className="panel-title">Subscriber Details</span>
                <table className="table table-condensed table-hover bg-white usertable mt-2">
                  <tbody>
                    <tr>
                      <td width="130">Lead Status</td>
                      <td onClick={() => handleToggleEdit("lead_status")}>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : editField === "lead_status" ? (
                          <select
                            className="form-control"
                            value={formValues.lead_status}
                            onChange={(e) =>
                              handleChange("lead_status", e.target.value)
                            }
                            onBlur={() => {
                              if (
                                formValues.lead_status === "" ||
                                formValues.lead_status ===
                                originalValues.lead_status
                              ) {
                                handleBlur("lead_status");
                              } else {
                                handleSave("lead_status");
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                if (
                                  formValues.lead_status === "" ||
                                  formValues.lead_status ===
                                  originalValues.lead_status
                                ) {
                                  handleBlur("lead_status");
                                } else {
                                  handleSave("lead_status");
                                }
                              }
                            }}
                            autoFocus
                          >
                            <option value="">Select</option>
                            {dropdownOptions.leadStatusList.map((opt) => (
                              <option key={opt.pick_name} value={opt.pick_name}>
                                {opt.pick_name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span>
                            {
                              dropdownOptions.leadStatusList.find(
                                (opt) => opt.pick_name == formValues.lead_status
                              )?.pick_name
                            }
                          </span>
                        )}
                      </td>
                    </tr>

                    <tr>
                      <td>Next Followup</td>
                      <td onClick={() => handleToggleEdit("next_followup")}>
                        {loading || isRendering ? (
                          <div
                            className="skeleton-text"
                            style={{
                              width: "100%",
                              height: "12px",
                              backgroundColor: "#e0e0e0",
                            }}
                          ></div>
                        ) : editField === "next_followup" ? (
                          <DatePicker
                            selected={
                              formValues.next_followup
                                ? new Date(formValues.next_followup)
                                : null
                            }
                            onChange={(date) =>
                              handleChange(
                                "next_followup",
                                moment(date).format("YYYY-MM-DD")
                              )
                            }
                            onBlur={() => {
                              if (
                                formValues.next_followup === "" ||
                                formValues.next_followup ===
                                originalValues.next_followup
                              ) {
                                handleBlur("next_followup");
                              } else {
                                handleSave("next_followup");
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                if (
                                  formValues.next_followup === "" ||
                                  formValues.next_followup ===
                                  originalValues.next_followup
                                ) {
                                  handleBlur("next_followup");
                                } else {
                                  handleSave("next_followup");
                                }
                              }
                            }}
                            className="form-control"
                            dateFormat="dd-MM-yyyy"
                            autoFocus
                          />
                        ) : (
                          <span className="value">
                            {formValues.next_followup &&
                              formValues.next_followup !==
                              "0000-00-00 00:00:00" &&
                              formValues.next_followup !==
                              "1899-11-29T18:38:50.000Z"
                              ? new Date(formValues.next_followup)
                                .toLocaleDateString("en-GB")
                                .replace(/\//g, "-")
                              : ""}
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="col-md-12">
              <div className="panel panel-default with-rounded-corner with-shadow">
                <div
                  className="panel-heading d-flex justify-content-between align-items-center"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span className="panel-title">History</span>

                  {subHistoryList &&
                    subHistoryList.length > historyItemsPerPage && (
                      <div
                        className="pagination-container"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <span style={{ fontSize: "14px", color: "#666" }}>
                          Page {currentHistoryPage} of{" "}
                          {Math.ceil(
                            subHistoryList.length / historyItemsPerPage
                          )}
                        </span>
                        <button
                          className="btn btn-default btn-sm"
                          onClick={() =>
                            setCurrentHistoryPage((prev) =>
                              Math.max(prev - 1, 1)
                            )
                          }
                          disabled={currentHistoryPage === 1}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <button
                          className="btn btn-default btn-sm"
                          onClick={() =>
                            setCurrentHistoryPage((prev) =>
                              Math.min(
                                prev + 1,
                                Math.ceil(
                                  subHistoryList.length / historyItemsPerPage
                                )
                              )
                            )
                          }
                          disabled={
                            currentHistoryPage ===
                            Math.ceil(
                              subHistoryList.length / historyItemsPerPage
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </div>
                    )}
                </div>

                <div className="panel-body">
                  <table
                    id="historytbl"
                    className="table table-condensed table-hover bg-white"
                    style={{ width: "100%" }}
                  >
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Active Date</th>
                        <th>Expiry Date</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading || isRendering ? (
                        [...Array(3)].map((_, i) => (
                          <tr key={i} className="skeleton-row">
                            <td>
                              <div className="skeleton-text" style={{ width: "20px" }}></div>
                            </td>
                            <td>
                              <div className="skeleton-text" style={{ width: "80px" }}></div>
                            </td>
                            <td>
                              <div className="skeleton-text" style={{ width: "80px" }}></div>
                            </td>
                            <td>
                              <div className="skeleton-text" style={{ width: "60px" }}></div>
                            </td>
                          </tr>
                        ))
                      ) : subHistoryList && subHistoryList.length > 0 ? (
                        subHistoryList
                          .slice(
                            (currentHistoryPage - 1) * historyItemsPerPage,
                            currentHistoryPage * historyItemsPerPage
                          )
                          .map((row, index) => (
                            <tr key={index}>
                              <td>
                                {(currentHistoryPage - 1) *
                                  historyItemsPerPage +
                                  index +
                                  1}
                              </td>
                              <td>{row.activedate}</td>
                              <td>{row.expirydate}</td>
                              <td>{row.amount || "-"}</td>
                            </tr>
                          ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center">
                            No history records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-7">
            <div className="row">
              <div className="col-md-12">
                {loading || isRendering ? (
                  <div className="panel panel-default with-rounded-corner with-shadow perform-detail p-10">
                    <div className="box p-4">
                      <div className="row">
                        <div className="col-md-12">
                          <div
                            className="skeleton-block"
                            style={{
                              width: "100%",
                              height: "300px",
                              marginBottom: "20px",
                            }}
                          ></div>
                        </div>
                      </div>
                      <div className="row">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="col-md-4">
                            <div
                              className="skeleton-text"
                              style={{ width: "80%", height: "14px" }}
                            ></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <PerformanceComparisonChart
                    subscriberid={subscription.subscriberid}
                    serviceid={subscription.serviceid}
                  />
                )}
                {/* <div className="panel panel-default with-rounded-corner with-shadow perform-detail p-10">
                  <div className="box">
                    <div className="row">
                      <div className="col-md-6 ps-5">
                        <MorrisDonutChart performanceData={performanceData} />
                      </div>
                      <div className="col-md-6 pe-5 pt-3">
                        <CircularProgress
                          label="Accuracy"
                          percent={performanceData.accuracy}
                        />
                      </div>
                    </div>
                    <div className="summarylist">
                      <ul>
                        <li>
                          <FontAwesomeIcon
                            icon={faCheckSquare}
                            className="text-primary"
                          />{" "}
                          Total Calls :{" "}
                          <span style={{ color: "#3484cf" }}>
                            {performanceData.totalcount}
                          </span>
                        </li>
                        <li>
                          <FontAwesomeIcon
                            icon={faCheckSquare}
                            className="text-primary"
                          />{" "}
                          Profitable Calls :{" "}
                          <span style={{ color: "#6fca00" }}>
                            {performanceData.positivecount}
                          </span>
                        </li>
                        <li>
                          <FontAwesomeIcon
                            icon={faCheckSquare}
                            className="text-primary"
                          />{" "}
                          Loss Calls :{" "}
                          <span style={{ color: "#e03e25" }}>
                            {performanceData.negativecount}
                          </span>
                        </li>
                      </ul>
                      <ul>
                        <li>
                          <FontAwesomeIcon
                            icon={faCheckSquare}
                            className="text-primary"
                          />{" "}
                          Total Profit :{" "}
                          <span style={{ color: "#6fca00" }}>
                            {performanceData.totalprofit}
                          </span>
                        </li>
                        <li>
                          <FontAwesomeIcon
                            icon={faCheckSquare}
                            className="text-primary"
                          />{" "}
                          Total Loss :{" "}
                          <span style={{ color: "#e03e25" }}>
                            {performanceData.totalloss}
                          </span>
                        </li>
                        <li>
                          <FontAwesomeIcon
                            icon={faCheckSquare}
                            className="text-primary"
                          />{" "}
                          Net Profit :{" "}
                          <span style={{ color: "#6fca00" }}>
                            {performanceData.avgprofit}
                          </span>
                        </li>
                      </ul>
                      <div className="clearfix"></div>
                    </div>
                  </div>
                </div> */}
              </div>

              <div className="col-md-6">
                <div className="panel panel-default with-rounded-corner with-shadow pb-2">
                  <div className="panel-heading">
                    <span className="panel-title">Lead Comments</span>
                  </div>
                  <div className="panel-body">
                    <textarea
                      cols="40"
                      rows="10"
                      placeholder="Enter Lead Comments"
                      className="form-control"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          postComment();
                        }
                      }}
                      style={{ height: "100px" }}
                    ></textarea>
                  </div>
                  <div className="clearfix"></div>
                  <div
                    id="commentTarget"
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      // padding: "15px",
                    }}
                  >
                    {loading || isRendering ? (
                      <div className="p-2">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="mb-3">
                            <div
                              className="skeleton-text"
                              style={{
                                width: "90%",
                                height: "16px",
                                marginBottom: "5px",
                              }}
                            ></div>
                            <div
                              className="skeleton-text"
                              style={{ width: "40%", height: "12px" }}
                            ></div>
                            <hr />
                          </div>
                        ))}
                      </div>
                    ) : commentList ? (
                      commentList.map((comment, index) => {
                        const createdon = comment.created_on
                          ? moment(comment.created_on).format(
                            "DD/MM/YYYY hh:mm A"
                          )
                          : "Unknown date";

                        return (
                          <div key={index} className="commentbox">
                            {comment.comments} <br />
                            <p className="workby">
                              By - {comment.created_by || "Unknown User"}
                            </p>
                            <p className="ondate">{createdon}</p>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-center text-muted">
                        No Comments Added Yet
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="panel panel-default with-rounded-corner with-shadow pb-2">
                  <div className="panel-heading border-0">
                    <span className="panel-title">Activity History</span>
                  </div>

                  <div id="activityTarget">
                    <div
                      className="commentbox"
                      id="activityData"
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                      }}
                    >
                      {loading || isRendering ? (
                        <div className="p-2">
                          {[...Array(5)].map((_, i) => (
                            <div key={i} className="mb-3">
                              <div
                                className="skeleton-text"
                                style={{
                                  width: "100%",
                                  height: "14px",
                                  marginBottom: "5px",
                                }}
                              ></div>
                              <div
                                className="skeleton-text"
                                style={{ width: "70%", height: "12px" }}
                              ></div>
                              <hr />
                            </div>
                          ))}
                        </div>
                      ) : activityList.length > 0 ? (
                        activityList.map((activity, index) => (
                          <p key={index}>
                            <span
                              style={{ color: "slategray", fontWeight: "bold" }}
                            >
                              {activity.workBy_user_fullname} Updated{" "}
                              {activity.colname}
                            </span>{" "}
                            <br /> {activity.message}
                            <span style={{ color: "blue", fontWeight: "bold" }}>
                              {" "}
                              on{" "}
                              {activity.created_on
                                ? moment(activity.created_on).format(
                                  "DD-MM-YYYY hh:mm A"
                                )
                                : ""}
                            </span>
                          </p>
                        ))
                      ) : (
                        <p className="pl-3">No Activities Done</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionEdit;
